import { useEffect, useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import FavoritesMenu from '@/components/feature/FavoritesMenu';

const navLinks = [
  { label: '首页', to: '/' },
  { label: '娱乐新闻', to: '/category/news' },
  { label: '明星动态', to: '/category/star' },
  { label: '网络热点', to: '/category/hot' },
];

export default function Navbar() {
  const location = useLocation();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [keyword, setKeyword] = useState('');

  const isHome = location.pathname === '/';
  const solid = scrolled || !isHome;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const submitSearch = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const q = keyword.trim();
    navigate(q ? `/search?q=${encodeURIComponent(q)}` : '/search');
    setKeyword('');
  };

  const goTopics = () => {
    setMenuOpen(false);
    if (isHome) {
      document.getElementById('topics')?.scrollIntoView({ behavior: 'smooth' });
    } else {
      navigate('/');
      window.setTimeout(() => {
        document.getElementById('topics')?.scrollIntoView({ behavior: 'smooth' });
      }, 200);
    }
  };

  const textClass = solid ? 'text-foreground-800' : 'text-background-50';
  const linkActive = (to: string) =>
    to === '/'
      ? location.pathname === '/'
      : location.pathname.startsWith(to.split('#')[0]) && to !== '/'
        ? location.pathname.startsWith(to)
        : false;

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${
        solid
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200'
          : 'bg-gradient-to-b from-foreground-950/70 via-foreground-950/25 to-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-8 h-16 flex items-center gap-6">
        <Link to="/" className="flex items-center gap-2 shrink-0 cursor-pointer">
          <span className="w-9 h-9 rounded-lg bg-primary-500 flex items-center justify-center text-background-50 font-heading font-black text-base">
            91
          </span>
          <span
            className={`font-heading font-black text-xl tracking-tight ${
              solid ? 'text-foreground-950' : 'text-background-50'
            }`}
          >
            吃瓜
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-1 ml-2">
          {navLinks.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className={`px-3 py-2 text-sm font-medium rounded-md transition-colors cursor-pointer ${textClass} ${
                linkActive(link.to)
                  ? 'text-primary-500'
                  : solid
                    ? 'hover:text-primary-500 hover:bg-primary-50'
                    : 'hover:text-primary-200'
              }`}
            >
              {link.label}
            </Link>
          ))}
          <button
            type="button"
            onClick={goTopics}
            className={`px-3 py-2 text-sm font-medium rounded-md transition-colors cursor-pointer ${
              solid
                ? 'text-foreground-800 hover:text-primary-500 hover:bg-primary-50'
                : 'text-background-50 hover:text-primary-200'
            }`}
          >
            专题
          </button>
        </nav>

        <form
          onSubmit={submitSearch}
          className="hidden md:flex items-center ml-auto relative"
        >
          <i className="ri-search-line absolute left-3 text-sm text-foreground-400 pointer-events-none" />
          <input
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            type="search"
            placeholder="搜索明星、剧集、热点"
            className={`w-56 lg:w-72 h-9 pl-9 pr-3 text-sm rounded-full border outline-none transition-colors ${
              solid
                ? 'bg-background-100 border-background-200 text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:bg-background-50'
                : 'bg-background-50/20 border-background-50/40 text-background-50 placeholder:text-background-50/70 focus:border-primary-300'
            }`}
          />
        </form>

        <div className="hidden md:flex items-center gap-3 ml-3">
          <FavoritesMenu solid={solid} />
          <Link
            to="/about"
            className="text-sm font-medium px-4 py-2 rounded-md bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            关于我们
          </Link>
        </div>

        <button
          type="button"
          onClick={() => setMenuOpen((v) => !v)}
          aria-label="打开菜单"
          className={`md:hidden ml-auto w-9 h-9 flex items-center justify-center rounded-md ${
            solid ? 'text-foreground-900' : 'text-background-50'
          }`}
        >
          <i className={menuOpen ? 'ri-close-line text-2xl' : 'ri-menu-line text-2xl'} />
        </button>
      </div>

      {menuOpen && (
        <div className="md:hidden bg-background-50 border-b border-background-200 animate-slide-down">
          <div className="px-4 py-3 flex flex-col gap-1">
            <form onSubmit={submitSearch} className="relative mb-2">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-sm text-foreground-400" />
              <input
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                type="search"
                placeholder="搜索明星、剧集、热点"
                className="w-full h-10 pl-9 pr-3 text-sm rounded-full border border-background-200 bg-background-100 text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400"
              />
            </form>
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className="px-3 py-2.5 text-sm font-medium text-foreground-800 rounded-md hover:bg-primary-50 hover:text-primary-600 cursor-pointer"
              >
                {link.label}
              </Link>
            ))}
            <button
              type="button"
              onClick={goTopics}
              className="px-3 py-2.5 text-left text-sm font-medium text-foreground-800 rounded-md hover:bg-primary-50 hover:text-primary-600 cursor-pointer"
            >
              专题
            </button>
            <Link
              to="/about"
              className="px-3 py-2.5 text-sm font-medium text-foreground-800 rounded-md hover:bg-primary-50 hover:text-primary-600 cursor-pointer"
            >
              关于我们
            </Link>
            <Link
              to="/contact"
              className="mt-1 text-center text-sm font-medium px-4 py-2.5 rounded-md bg-primary-500 text-background-50 hover:bg-primary-600 cursor-pointer"
            >
              联系我们
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}